import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { toast } from 'react-toastify';
import { disputesApi } from '../../api/disputes';
import { PageHeader, StatusBadge, ConfirmDialog } from '../../components/ui';
import { formatDate, formatDateTime } from '../../utils';
import type { Complaint, ComplaintStatus } from '../../types';

const COMPLAINT_STATUSES: ComplaintStatus[] = [
  'Open',
  'Under Review',
  'Resolved',
  'Rejected',
  'Closed',
];

const ComplaintDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [statusDraft, setStatusDraft] = useState<ComplaintStatus>('Open');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonAction, setReasonAction] = useState<'Resolved' | 'Rejected' | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [closeConfirm, setCloseConfirm] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await disputesApi.getDisputeDetails(id);
      setComplaint(data);
      setStatusDraft(data.status);
    } catch {
      toast.error('Complaint not found');
      navigate('/complaints');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdateStatus = async () => {
    if (!complaint || statusDraft === complaint.status) return;
    setSaving(true);
    try {
      toast.info('Complaint status update API is not available yet');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!complaint || !noteText.trim()) return;
    setSaving(true);
    try {
      toast.info('Complaint notes API is not available yet');
      setNoteText('');
      setNoteOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleReasonConfirm = async () => {
    if (!complaint || !reasonAction || !reasonText.trim()) return;
    if (!complaint.transactionId) {
      toast.error('Transaction ID missing for this dispute');
      return;
    }
    setSaving(true);
    try {
      if (reasonAction === 'Resolved') {
        await disputesApi.resolveDisputeUser(complaint.jobId, complaint.transactionId);
      } else {
        await disputesApi.resolveDisputeProvider(complaint.jobId, complaint.transactionId);
      }
      const updated = await disputesApi.getDisputeDetails(complaint.id);
      setComplaint({ ...updated, status: reasonAction, resolutionReason: reasonText.trim() });
      setStatusDraft(reasonAction);
      setReasonOpen(false);
      setReasonAction(null);
      setReasonText('');
      toast.success(`Dispute ${reasonAction.toLowerCase()}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (!complaint) return;
    setSaving(true);
    try {
      toast.info('Close dispute API is not available yet');
      setCloseConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !complaint) {
    return (
      <Box>
        <PageHeader title="Complaint Detail" />
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={complaint.id}
        subtitle={complaint.type}
        breadcrumbs={[
          { label: 'Complaints', to: '/complaints' },
          { label: complaint.id },
        ]}
        actions={
          <Button
            component={RouterLink}
            to="/complaints"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back
          </Button>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h3">Description</Typography>
                <StatusBadge status={complaint.status} />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {complaint.description}
              </Typography>
              {complaint.resolutionReason && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Resolution reason
                  </Typography>
                  <Typography variant="body2">{complaint.resolutionReason}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h3" sx={{ mb: 1.5 }}>
                Related Job
              </Typography>
              <Typography
                component={RouterLink}
                to={`/jobs/${complaint.jobId}`}
                variant="body2"
                fontWeight={600}
                sx={{ color: 'primary.main', textDecoration: 'none' }}
              >
                {complaint.jobId}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Created {formatDate(complaint.createdAt)}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Parties
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted by
                  </Typography>
                  <Typography fontWeight={600}>{complaint.submittedBy}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {complaint.submittedByType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Against
                  </Typography>
                  <Typography fontWeight={600}>{complaint.against}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {complaint.againstType}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 1.5 }}>
                Attachments
              </Typography>
              <Box
                sx={{
                  py: 4,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <AttachFileOutlinedIcon sx={{ color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No attachments uploaded
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Status History
              </Typography>
              <Stack spacing={2}>
                {complaint.history.map((event, index) => (
                  <Box key={event.id} sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: index === complaint.history.length - 1 ? 'primary.main' : 'divider',
                        mt: 0.75,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography fontWeight={600} variant="body2">
                        {event.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(event.date)}
                      </Typography>
                      {event.note && (
                        <Typography variant="body2" color="text.secondary">
                          {event.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
              {complaint.notes.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h3" sx={{ mb: 1.5 }}>
                    Internal Notes
                  </Typography>
                  <Stack spacing={1.5}>
                    {complaint.notes.map((note) => (
                      <Box
                        key={note.id}
                        sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}
                      >
                        <Typography variant="body2">{note.text}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(note.createdAt)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, position: 'sticky', top: 24 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Actions
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value as ComplaintStatus)}
                >
                  {COMPLAINT_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="contained"
                disabled={saving || statusDraft === complaint.status}
                onClick={handleUpdateStatus}
                sx={{ mb: 1.5 }}
              >
                Update Status
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setNoteOpen(true)}
                sx={{ mb: 1.5 }}
              >
                Add Internal Note
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="success"
                disabled={complaint.status === 'Resolved' || complaint.status === 'Closed'}
                onClick={() => {
                  setReasonAction('Resolved');
                  setReasonText('');
                  setReasonOpen(true);
                }}
                sx={{ mb: 1.5 }}
              >
                Resolve
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                disabled={complaint.status === 'Rejected' || complaint.status === 'Closed'}
                onClick={() => {
                  setReasonAction('Rejected');
                  setReasonText('');
                  setReasonOpen(true);
                }}
                sx={{ mb: 1.5 }}
              >
                Reject
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                disabled={complaint.status === 'Closed'}
                onClick={() => setCloseConfirm(true)}
              >
                Close
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={noteOpen} onClose={() => !saving && setNoteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Internal Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={saving || !noteText.trim()}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reasonOpen} onClose={() => !saving && setReasonOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {reasonAction === 'Resolved' ? 'Resolve Complaint' : 'Reject Complaint'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReasonOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reasonAction === 'Rejected' ? 'error' : 'secondary'}
            onClick={handleReasonConfirm}
            disabled={saving || !reasonText.trim()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={closeConfirm}
        title="Close Complaint"
        message="Closing this complaint will mark it as final. Continue?"
        confirmLabel="Close"
        danger
        loading={saving}
        onConfirm={handleClose}
        onClose={() => setCloseConfirm(false)}
      />
    </Box>
  );
};

export default ComplaintDetailPage;
